// Web Push: lets likes/follows/messages/trending alerts reach the user even
// when the app is closed or the phone is asleep — the OS delivers them to
// the service worker (see public/sw.js's 'push' handler), which is why this
// only works once a subscription has actually been registered with the
// browser's push service AND saved on our server (POST /api/push).
//
// Falls back to being a complete no-op everywhere unsupported (desktop
// Safari, some in-app browsers) — the in-app notification bell keeps
// working regardless, this is purely additive.

import { apiGet, apiPost, apiDelete } from './api.js';

function urlBase64ToUint8Array(base64url) {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Current status for UI purposes: 'unsupported' | 'default' | 'denied' | 'granted'
export function pushPermissionState() {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

// Safe to call anytime (e.g. right after login) — only actually subscribes
// if permission was already granted in a *previous* visit; never prompts on
// its own (browsers require a user gesture for that — see requestPushPermission).
export async function syncPushSubscription() {
  if (!pushSupported() || Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { vapid_public_key } = await apiGet('/api/config');
      if (!vapid_public_key) return; // server not configured yet
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid_public_key),
      });
    }
    const json = sub.toJSON();
    await apiPost('/api/push', { endpoint: json.endpoint, keys: json.keys });
  } catch (e) { /* non-fatal — in-app notifications still work */ }
}

// Must be called directly from a click handler — that's what lets the
// browser show its native permission prompt at all.
export async function requestPushPermission() {
  if (!pushSupported()) return 'unsupported';
  const result = await Notification.requestPermission();
  if (result === 'granted') await syncPushSubscription();
  return result;
}

// Called on sign-out so a shared/public device stops receiving this
// account's pushes the moment they leave.
export async function clearPushSubscription() {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await apiDelete('/api/push?endpoint=' + encodeURIComponent(sub.endpoint)).catch(() => {});
      await sub.unsubscribe();
    }
  } catch (e) { }
}
